import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import systemStatusService, {
  type DailyHistoryRow,
  type RecentProbeRow,
} from '@/services/systemStatus.service';

const { Title, Text } = Typography;

function formatUptime(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  const mb = n / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(1)} MB`;
}

function dayColor(status: DailyHistoryRow['status']): string {
  if (status === 'healthy') return '#52c41a';
  if (status === 'degraded') return '#faad14';
  if (status === 'unhealthy') return '#ff4d4f';
  return '#d9d9d9';
}

const SystemStatusPage: React.FC = () => {
  const qc = useQueryClient();
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['platform-system-status'],
    queryFn: () => systemStatusService.getStatus(),
    refetchInterval: 60_000,
  });

  const probeMut = useMutation({
    mutationFn: () => systemStatusService.recordProbe(),
    onSuccess: () => {
      message.success('Probe recorded');
      qc.invalidateQueries({ queryKey: ['platform-system-status'] });
    },
    onError: (e: Error) => message.error(e.message || 'Probe failed'),
  });

  const live = data?.live;
  const summary = data?.summary;
  const history = data?.history || [];
  const recent = data?.recent_probes || [];

  const healthy = live?.status === 'healthy';

  const historyTip = useMemo(() => {
    return (row: DailyHistoryRow) => {
      if (row.probes === 0) return `${row.day}: no probes`;
      return `${row.day}: ${row.uptime_pct ?? 0}% (${row.healthy}/${row.probes} healthy)`;
    };
  }, []);

  const recentColumns = [
    {
      title: 'Checked',
      dataIndex: 'checked_at',
      key: 'checked_at',
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: 'Status',
      dataIndex: 'overall_status',
      key: 'overall_status',
      width: 120,
      render: (v: string) =>
        v === 'healthy' ? <Tag color="success">Healthy</Tag> : <Tag color="error">Unhealthy</Tag>,
    },
    {
      title: 'Database',
      dataIndex: 'database_ok',
      key: 'database_ok',
      width: 110,
      render: (v: boolean) => (v ? <Tag color="green">OK</Tag> : <Tag color="red">Down</Tag>),
    },
    {
      title: 'Latency',
      dataIndex: 'latency_ms',
      key: 'latency_ms',
      width: 100,
      render: (v: number | null) => (v != null ? `${v} ms` : '—'),
    },
    {
      title: 'Process uptime',
      dataIndex: 'uptime_seconds',
      key: 'uptime_seconds',
      width: 140,
      render: (v: number | null) => (v != null ? formatUptime(v) : '—'),
    },
    {
      title: 'Version',
      dataIndex: 'build_version',
      key: 'build_version',
      ellipsis: true,
      render: (v: string | null) => v || '—',
    },
  ];

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Could not load system status"
          description={(error as Error)?.message || 'Request failed'}
          action={
            <Button onClick={() => refetch()} icon={<ReloadOutlined />}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            System Status
          </Title>
          <Text type="secondary">Deployment health, process uptime, and probe history</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<CloudServerOutlined />}
            loading={probeMut.isPending}
            onClick={() => probeMut.mutate()}
          >
            Refresh &amp; record
          </Button>
        </Space>
      </Space>

      <Alert
        style={{ marginBottom: 16 }}
        type={healthy ? 'success' : 'error'}
        showIcon
        icon={healthy ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        message={healthy ? 'All systems healthy' : 'System unhealthy'}
        description={
          live
            ? `Checked ${new Date(live.timestamp).toLocaleString()} · DB ${live.database} · latency ${
                live.latency_ms != null ? `${live.latency_ms} ms` : '—'
              }`
            : undefined
        }
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="API / process"
              value={healthy ? 'Healthy' : 'Unhealthy'}
              prefix={healthy ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
              valueStyle={{ color: healthy ? '#3f8600' : '#cf1322', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Database"
              value={live?.database === 'connected' ? 'Connected' : 'Disconnected'}
              prefix={<DatabaseOutlined />}
              valueStyle={{
                color: live?.database_ok ? '#3f8600' : '#cf1322',
                fontSize: 20,
              }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="Process uptime" value={formatUptime(live?.uptime || 0)} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="Build version" value={live?.version || '—'} valueStyle={{ fontSize: 18 }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic title="Environment" value={live?.environment || '—'} valueStyle={{ fontSize: 18 }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Heap used"
              value={live?.memory ? formatBytes(live.memory.heapUsed) : '—'}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Uptime 30d"
              value={summary?.uptime_pct_30d != null ? summary.uptime_pct_30d : '—'}
              suffix={summary?.uptime_pct_30d != null ? '%' : undefined}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Card size="small">
            <Statistic
              title="Uptime 90d"
              value={summary?.uptime_pct_90d != null ? summary.uptime_pct_90d : '—'}
              suffix={summary?.uptime_pct_90d != null ? '%' : undefined}
            />
          </Card>
        </Col>
      </Row>

      {summary?.last_outage_at ? (
        <Alert
          style={{ marginBottom: 16 }}
          type="warning"
          showIcon
          message="Last recorded unhealthy probe"
          description={new Date(summary.last_outage_at).toLocaleString()}
        />
      ) : null}

      <Card title="90-day uptime calendar" style={{ marginBottom: 16 }} size="small">
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 4,
            maxWidth: 920,
          }}
        >
          {history.map((row) => (
            <Tooltip key={row.day} title={historyTip(row)}>
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: dayColor(row.status),
                }}
              />
            </Tooltip>
          ))}
        </div>
        <Space style={{ marginTop: 12 }} size="large">
          <Text type="secondary">
            <span style={{ display: 'inline-block', width: 10, height: 10, background: '#52c41a', marginRight: 6 }} />
            Healthy
          </Text>
          <Text type="secondary">
            <span style={{ display: 'inline-block', width: 10, height: 10, background: '#faad14', marginRight: 6 }} />
            Degraded
          </Text>
          <Text type="secondary">
            <span style={{ display: 'inline-block', width: 10, height: 10, background: '#ff4d4f', marginRight: 6 }} />
            Unhealthy
          </Text>
          <Text type="secondary">
            <span style={{ display: 'inline-block', width: 10, height: 10, background: '#d9d9d9', marginRight: 6 }} />
            No data
          </Text>
        </Space>
      </Card>

      <Card title="Recent probes (48h)" size="small">
        <Table<RecentProbeRow>
          rowKey="id"
          size="small"
          pagination={{ pageSize: 20, showSizeChanger: true }}
          dataSource={recent}
          columns={recentColumns}
          locale={{ emptyText: 'No probes yet — use Refresh & record or wait for the hourly job' }}
        />
      </Card>
    </div>
  );
};

export default SystemStatusPage;
